import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

interface Artist {
  _id: string;
  stageName: string;
  slug: string;
  affiliateCode: string;
  verified: boolean;
  totalPlays: number;
}

export default function Artists() {
  const qc = useQueryClient();
  const { data } = useQuery<{ items: Artist[] }>({
    queryKey: ['artists', 'all'],
    queryFn: () => api('/api/v1/artists?limit=100'),
  });
  const [showForm, setShowForm] = useState(false);
  const [stageName, setStageName] = useState('');
  const [bio, setBio] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api('/api/v1/admin/artists', { method: 'POST', body: JSON.stringify({ stageName, bio }) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['artists'] });
      setShowForm(false);
      setStageName('');
      setBio('');
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Artists</h1>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="bg-accent text-bg font-semibold px-4 py-2 rounded-md"
        >
          {showForm ? 'Cancel' : '+ New artist'}
        </button>
      </div>
      {showForm ? (
        <form onSubmit={submit} className="bg-surface border border-border rounded-xl p-6 mb-6">
          <input
            value={stageName}
            onChange={(e) => setStageName(e.target.value)}
            placeholder="Stage name"
            className="w-full mb-3 bg-bg border border-border rounded-md px-3 py-2"
          />
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Bio"
            className="w-full mb-3 bg-bg border border-border rounded-md px-3 py-2 h-24"
          />
          <button className="bg-accent text-bg px-4 py-2 rounded-md font-semibold" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </form>
      ) : null}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface2 text-left text-text2">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Affiliate code</th>
              <th className="px-4 py-3">Verified</th>
              <th className="px-4 py-3 text-right">Total plays</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((a) => (
              <tr key={a._id} className="border-t border-border">
                <td className="px-4 py-3 font-medium">{a.stageName}</td>
                <td className="px-4 py-3 font-mono text-accent">{a.affiliateCode}</td>
                <td className="px-4 py-3">{a.verified ? '✓' : '–'}</td>
                <td className="px-4 py-3 text-right">{a.totalPlays.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
